import { ReactNode, createContext, useCallback, useContext, useState } from 'react'
import {
  AppStatus,
  DraftSession,
  EndSessionMethod,
  Language,
  MultiLanguage,
  TemplateRoute,
} from 'types'
import { mergeObjects } from 'utils'
import { endSessionTracking } from '../../utils/helpers'
import { useExplorerSessionCache } from '../../utils/preferences'
import { getTrait } from '../../utils/traitUtils'
import { useExplorerConfig } from './ExplorerConfigProvider'
import { SelectionCountsContext } from './SelectionCountsProvider'

export interface ISessionContext {
  endSession: (method: EndSessionMethod, status?: AppStatus) => void
  getLanguageValue: <T>(body?: MultiLanguage<T>) => T
  language: Language
  mergeSession: (update: DraftSession) => void
  /** Reduced functionality for preview mode */
  preview: boolean
  /** split indexes taken by user */
  routeSteps: TemplateRoute[]
  session: DraftSession | null
  startSession: () => void
  status: AppStatus
  updateRouteSteps: (body: TemplateRoute[]) => void
  updateStatus: (status: AppStatus) => void
}

export const SessionContext = createContext<ISessionContext>({} as any)

interface Props {
  cache?: boolean
  children: ReactNode
  preview?: boolean
  status?: AppStatus
}

export function SessionContextProvider({
  cache,
  children,
  preview = false,
  status: _status,
}: Props) {
  const { templateDocs } = useExplorerConfig()
  const { updateCounts } = useContext(SelectionCountsContext)
  const [explorerState, setExplorerState] = useExplorerSessionCache()

  const [status, setStatus] = useState<AppStatus>(_status ?? explorerState?.status ?? 'init')
  const [routeSteps, setRouteSteps] = useState<TemplateRoute[]>(explorerState?.routeSteps ?? [])
  const [session, setSession] = useState<DraftSession | null>(explorerState?.session ?? null)
  const [language, setLanguage] = useState<Language>('en')

  function startSession() {
    if (!templateDocs) return

    const routeStep = templateDocs.template.routing
    if (!routeStep) return endSession('final-button')

    const currentTraitGroup = getTrait(templateDocs, routeStep.key)
    if (!currentTraitGroup) return console.error('Trait group not found')

    const { default: _, valueRoutes, ...firstStep } = routeStep
    const routeSteps = [firstStep]
    setRouteSteps(routeSteps)
    setStatus('active')
    if (cache) setExplorerState(s => ({ ...s, routeSteps, status: 'active' }))
  }

  function endSession(method: EndSessionMethod, status?: AppStatus) {
    const _status = status ?? 'screensaver'
    if (session?.traits) updateCounts(session.traits)

    setStatus(_status)
    endSessionTracking({ method, status: _status })
    setRouteSteps([])
    setSession(null)
    if (cache) setExplorerState({})
  }

  function mergeSession(update: DraftSession) {
    const data = session ? mergeObjects(session, update) : update
    setSession(data)
    if (cache) setExplorerState(s => ({ ...s, session: data }))
  }

  function updateRouteSteps(update: TemplateRoute[]) {
    setRouteSteps(update)
    if (cache) setExplorerState(s => ({ ...s, routeSteps: update }))
  }

  function updateStatus(status: AppStatus) {
    setStatus(status)
    if (cache) setExplorerState(s => ({ ...s, status }))
  }

  const getLanguageValue = useCallback(
    <T extends unknown>(body?: MultiLanguage<T>) => body?.[language] as T,
    [language]
  )

  return (
    <SessionContext.Provider
      value={{
        endSession,
        getLanguageValue,
        language,
        mergeSession,
        preview,
        routeSteps,
        session,
        startSession,
        status,
        updateRouteSteps,
        updateStatus,
      }}
    >
      {children}
    </SessionContext.Provider>
  )
}
